/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import * as Sentry from '@sentry/browser';
import appSettings from '../config/settings';
import { assertValidEnvironment, assertValidSampleRate } from './environment';

const ingoreErrorsMap = {
  ['ResizeObserver loop limit exceeded']: true, // Some exotic browsers seems to emit these.
  ['Error reading']: true, // Ignore file reader errors (ImageFromFile)
  ['AxiosError: Network Error']: true,
  ['Request aborted']: true, // User navigated away before a request finished
  ['The connection was closed']: true, // AbortError on a closed connection during navigation
};

const pickSelectedErrors = (ignored, entry) => {
  const [key, value] = entry;
  return value === true ? [...ignored, key] : ignored;
};

// Redact auth tokens, API keys, and PII from events + breadcrumbs before they leave the browser.
const SENSITIVE_KEY_RE = /(token|authorization|password|secret|api[-_]?key|cookie|email|phone|address|postal|pickup[-_]?code|card|cvc|iban|ssn)/i;
const SECRET_VALUE_RE = /(sk_live|sk_test|rk_live|pk_live|whsec_|eyJ[A-Za-z0-9_-]{10,})/;

const scrubObject = value => {
  if (!value || typeof value !== 'object') return value;
  Object.keys(value).forEach(key => {
    const child = value[key];
    if (SENSITIVE_KEY_RE.test(key)) {
      value[key] = '[Filtered]';
    } else if (typeof child === 'string' && SECRET_VALUE_RE.test(child)) {
      value[key] = '[Filtered]';
    } else if (child && typeof child === 'object') {
      scrubObject(child);
    }
  });
  return value;
};

const scrubEvent = event => {
  if (event?.request?.cookies) event.request.cookies = '[Filtered]';
  scrubObject(event?.request?.headers);
  scrubObject(event?.request?.data);
  scrubObject(event?.extra);
  scrubObject(event?.contexts);
  return event;
};

const scrubBreadcrumb = breadcrumb => {
  scrubObject(breadcrumb?.data);
  return breadcrumb;
};

/**
 * Set up error handling. If a Sentry DSN is
 * provided a Sentry client will be installed with
 * distributed tracing, session replay, and PII scrubbing.
 */
export const setup = () => {
  if (appSettings.sentryDsn) {
    const ignoreErrors = Object.entries(ingoreErrorsMap).reduce(pickSelectedErrors, []);
    const environment = assertValidEnvironment(appSettings.sentryEnvironment);

    // Configures the Sentry client. Adds a handler for
    // any uncaught exception.
    Sentry.init({
      dsn: appSettings.sentryDsn,
      environment,
      release: appSettings.sentryRelease,
      ignoreErrors,
      integrations: [
        Sentry.browserTracingIntegration(),
        // Show page text so replays keep their context; still mask every form
        // input (passwords, email, phone, payment) and any element opted in via
        // the .sentry-mask class / data-sentry-mask attribute.
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: assertValidSampleRate(
        'REACT_APP_SENTRY_TRACES_SAMPLE_RATE',
        appSettings.sentryTracesSampleRate
      ),
      replaysSessionSampleRate: assertValidSampleRate(
        'REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
        appSettings.sentryReplaysSessionSampleRate
      ),
      replaysOnErrorSampleRate: assertValidSampleRate(
        'REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE',
        appSettings.sentryReplaysOnErrorSampleRate
      ),
      sendDefaultPii: false,
      beforeSend: scrubEvent,
      beforeBreadcrumb: scrubBreadcrumb,
    });
  }
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {String} userId ID of current user
 */
export const setUserId = userId => {
  Sentry.setUser({ id: userId });
};

/**
 * Clears the user ID.
 */

export const clearUserId = () => {
  Sentry.setUser(null);
};

const printAPIErrorsAsConsoleTable = apiErrors => {
  /* eslint-disable no-console */
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    console.log('Errors returned by Marketplace API call:');
    console.table(
      apiErrors.map(err => ({
        status: err.status,
        code: err.code,
        details: err.details,
        ...err.meta,
      }))
    );
  }
  /* eslint-enable no-console */
};

const responseAPIErrors = error => {
  return error && error.data && error.data.errors ? error.data.errors : [];
};

const responseApiErrorInfo = err =>
  responseAPIErrors(err).map(e => ({
    status: e.status,
    code: e.code,
    meta: e.meta,
    details: e.details,
  }));

/**
 * Ensures Sentry receives a real Error. storableError and axios-style errors
 * are plain objects, which Sentry captures as an unreadable
 * "Object captured as exception with keys: ..." title. Wrapping them in an
 * Error surfaces the actual message (e.g. "Network Error") instead.
 *
 * @param {*} e value passed to log.error
 * @returns {Error}
 */
const toCapturableError = e => {
  if (e instanceof Error) return e;
  if (e && typeof e === 'object') {
    const err = new Error(e.message || e.statusText || 'Unknown error');
    if (e.name) err.name = e.name;
    if (e.status != null) err.status = e.status;
    if (e.type) err.type = e.type;
    return err;
  }
  return new Error(String(e));
};

/**
 * Logs an exception. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * @param {Error} e Error that occurred
 * @param {String} code Error code
 * @param {Object} data Additional data to be sent to Sentry
 */
export const error = (e, code, data) => {
  const apiErrors = responseApiErrorInfo(e);
  if (appSettings.sentryDsn) {
    const extra = { ...data, apiErrorData: apiErrors };

    Sentry.withScope(scope => {
      scope.setTag('code', code);
      // Group by the error code so distinct failures do not merge into one
      // opaque catch-all issue (SDK AxiosErrors otherwise share a stack frame
      // and fingerprint together regardless of what actually failed).
      if (code) scope.setFingerprint(['{{ default }}', code]);
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(toCapturableError(e));
    });

    printAPIErrorsAsConsoleTable(apiErrors);
  } else {
    console.error(e);
    console.error('Error code:', code, 'data:', data);
    printAPIErrorsAsConsoleTable(apiErrors);
  }
};

/**
 * Logs an expected, user-facing failure (e.g. a declined card) at Sentry
 * `warning` level, so it stays visible and trackable without firing as an
 * error alert. Same arguments as `error`.
 *
 * @param {Error} e Error that occurred
 * @param {String} code Error code
 * @param {Object} data Additional data to be sent to Sentry
 */
export const warn = (e, code, data) => {
  const apiErrors = responseApiErrorInfo(e);
  if (appSettings.sentryDsn) {
    const extra = { ...data, apiErrorData: apiErrors };

    Sentry.withScope(scope => {
      scope.setLevel('warning');
      scope.setTag('code', code);
      // Group by the error code so distinct failures do not merge into one
      // opaque catch-all issue (SDK AxiosErrors otherwise share a stack frame
      // and fingerprint together regardless of what actually failed).
      if (code) scope.setFingerprint(['{{ default }}', code]);
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(toCapturableError(e));
    });

    printAPIErrorsAsConsoleTable(apiErrors);
  } else {
    console.warn(e);
    console.warn('Error code:', code, 'data:', data);
    printAPIErrorsAsConsoleTable(apiErrors);
  }
};

const setCause = (error, cause) => {
  const seenErrors = new WeakSet();

  const setCauseIfNoExistingCause = (error, cause) => {
    if (seenErrors.has(error)) {
      return;
    }
    if (error.cause) {
      seenErrors.add(error);
      return setCauseIfNoExistingCause(error.cause, cause);
    }
    error.cause = cause;
  };

  setCauseIfNoExistingCause(error, cause);
};

export const onRecoverableError = (e, componentStack) => {
  let data = {};

  if (componentStack) {
    // Generating this synthetic error allows monitoring services to apply sourcemaps
    // to unminify the stacktrace and make it readable.
    const errorBoundaryError = new Error(e.message);
    errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
    errorBoundaryError.stack = componentStack;

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
    setCause(e, errorBoundaryError);

    data.componentStack = componentStack;
  }

  error(e, 'recoverable-error', data);
};
